/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  DeleteTenantPathParameters,
  GetAllTenantsParams,
  GetTenantPathParameters,
  PermanentlyDeleteTenantPathParameters,
  RestoreTenantPathParameters,
  TLHTTPValidationError,
  TLSuccessResponse,
  TLTenant,
  TLTenantCreate,
  TLTenantPurgeResponse,
  TLTenantUpdate,
  TLTenantVisibilityUpdateRequest,
  UpdateTenantPathParameters,
  UpdateTenantVisibilityPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get All Tenants
 */
export const getAllTenants = (
  params?: GetAllTenantsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLTenant[]>({ url: `/tenants`, method: 'GET', params, signal }, options);
};

export const getGetAllTenantsQueryKey = (params?: GetAllTenantsParams) => {
  return ['tenants', ...(params ? [params] : [])] as const;
};

export const getGetAllTenantsQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllTenants>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllTenantsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllTenants>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllTenantsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllTenants>>> = ({ signal }) =>
    getAllTenants(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllTenants>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllTenantsQueryResult = NonNullable<Awaited<ReturnType<typeof getAllTenants>>>;
export type GetAllTenantsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetAllTenants<
  TData = Awaited<ReturnType<typeof getAllTenants>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetAllTenantsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllTenants>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllTenants>>,
          TError,
          Awaited<ReturnType<typeof getAllTenants>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllTenants<
  TData = Awaited<ReturnType<typeof getAllTenants>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllTenantsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllTenants>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllTenants>>,
          TError,
          Awaited<ReturnType<typeof getAllTenants>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllTenants<
  TData = Awaited<ReturnType<typeof getAllTenants>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllTenantsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllTenants>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get All Tenants
 */

export function useGetAllTenants<
  TData = Awaited<ReturnType<typeof getAllTenants>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllTenantsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllTenants>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllTenantsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get All Tenants
 */
export const prefetchGetAllTenantsQuery = async <
  TData = Awaited<ReturnType<typeof getAllTenants>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetAllTenantsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllTenants>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetAllTenantsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get All Tenants
 */
export const invalidateGetAllTenants = async (
  queryClient: QueryClient,
  params?: GetAllTenantsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetAllTenantsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Create Tenant
 */
export const createTenant = (
  tLTenantCreate: BodyType<TLTenantCreate>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLTenant>(
    { url: `/tenants`, method: 'POST', headers: { 'Content-Type': 'application/json' }, data: tLTenantCreate, signal },
    options,
  );
};

export const getCreateTenantQueryKey = (tLTenantCreate?: BodyType<TLTenantCreate>) => {
  return ['POST', 'tenants', tLTenantCreate] as const;
};

export const getCreateTenantQueryOptions = <
  TData = Awaited<ReturnType<typeof createTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTenantCreate: BodyType<TLTenantCreate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateTenantQueryKey(tLTenantCreate);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createTenant>>> = ({ signal }) =>
    createTenant(tLTenantCreate, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createTenant>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateTenantQueryResult = NonNullable<Awaited<ReturnType<typeof createTenant>>>;
export type CreateTenantQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateTenant<
  TData = Awaited<ReturnType<typeof createTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTenantCreate: BodyType<TLTenantCreate>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTenant>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createTenant>>,
          TError,
          Awaited<ReturnType<typeof createTenant>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateTenant<
  TData = Awaited<ReturnType<typeof createTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTenantCreate: BodyType<TLTenantCreate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTenant>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createTenant>>,
          TError,
          Awaited<ReturnType<typeof createTenant>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateTenant<
  TData = Awaited<ReturnType<typeof createTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTenantCreate: BodyType<TLTenantCreate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Tenant
 */

export function useCreateTenant<
  TData = Awaited<ReturnType<typeof createTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTenantCreate: BodyType<TLTenantCreate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateTenantQueryOptions(tLTenantCreate, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Tenant
 */
export const prefetchCreateTenantQuery = async <
  TData = Awaited<ReturnType<typeof createTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLTenantCreate: BodyType<TLTenantCreate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateTenantQueryOptions(tLTenantCreate, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Tenant
 */
export const invalidateCreateTenant = async (
  queryClient: QueryClient,
  tLTenantCreate: BodyType<TLTenantCreate>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateTenantQueryKey(tLTenantCreate) }, options);

  return queryClient;
};

/**
 * @summary Invalidate All Tenant Cache
 */
export const invalidateAllTenantCache = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLSuccessResponse>({ url: `/tenants/cache/invalidate-all`, method: 'POST', signal }, options);
};

export const getInvalidateAllTenantCacheQueryKey = () => {
  return ['POST', 'tenants', 'cache', 'invalidate-all'] as const;
};

export const getInvalidateAllTenantCacheQueryOptions = <
  TData = Awaited<ReturnType<typeof invalidateAllTenantCache>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof invalidateAllTenantCache>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getInvalidateAllTenantCacheQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof invalidateAllTenantCache>>> = ({ signal }) =>
    invalidateAllTenantCache(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof invalidateAllTenantCache>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type InvalidateAllTenantCacheQueryResult = NonNullable<Awaited<ReturnType<typeof invalidateAllTenantCache>>>;
export type InvalidateAllTenantCacheQueryError = ErrorType<unknown>;

export function useInvalidateAllTenantCache<
  TData = Awaited<ReturnType<typeof invalidateAllTenantCache>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof invalidateAllTenantCache>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof invalidateAllTenantCache>>,
          TError,
          Awaited<ReturnType<typeof invalidateAllTenantCache>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useInvalidateAllTenantCache<
  TData = Awaited<ReturnType<typeof invalidateAllTenantCache>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof invalidateAllTenantCache>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof invalidateAllTenantCache>>,
          TError,
          Awaited<ReturnType<typeof invalidateAllTenantCache>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useInvalidateAllTenantCache<
  TData = Awaited<ReturnType<typeof invalidateAllTenantCache>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof invalidateAllTenantCache>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Invalidate All Tenant Cache
 */

export function useInvalidateAllTenantCache<
  TData = Awaited<ReturnType<typeof invalidateAllTenantCache>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof invalidateAllTenantCache>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getInvalidateAllTenantCacheQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Invalidate All Tenant Cache
 */
export const prefetchInvalidateAllTenantCacheQuery = async <
  TData = Awaited<ReturnType<typeof invalidateAllTenantCache>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof invalidateAllTenantCache>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getInvalidateAllTenantCacheQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Invalidate All Tenant Cache
 */
export const invalidateInvalidateAllTenantCache = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getInvalidateAllTenantCacheQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Update Tenant
 */
export const updateTenant = (
  { tenantId }: UpdateTenantPathParameters,
  tLTenantUpdate: BodyType<TLTenantUpdate>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLTenant>(
    {
      url: `/tenants/update/${tenantId}`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLTenantUpdate,
      signal,
    },
    options,
  );
};

export const getUpdateTenantQueryKey = (
  { tenantId }: UpdateTenantPathParameters,
  tLTenantUpdate?: BodyType<TLTenantUpdate>,
) => {
  return ['POST', 'tenants', 'update', tenantId, tLTenantUpdate] as const;
};

export const getUpdateTenantQueryOptions = <
  TData = Awaited<ReturnType<typeof updateTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { tenantId }: UpdateTenantPathParameters,
  tLTenantUpdate: BodyType<TLTenantUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateTenantQueryKey({ tenantId }, tLTenantUpdate);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateTenant>>> = ({ signal }) =>
    updateTenant({ tenantId }, tLTenantUpdate, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: tenantId !== null && tenantId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateTenant>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateTenantQueryResult = NonNullable<Awaited<ReturnType<typeof updateTenant>>>;
export type UpdateTenantQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateTenant<
  TData = Awaited<ReturnType<typeof updateTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateTenantPathParameters,
  tLTenantUpdate: BodyType<TLTenantUpdate>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTenant>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateTenant>>,
          TError,
          Awaited<ReturnType<typeof updateTenant>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateTenant<
  TData = Awaited<ReturnType<typeof updateTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateTenantPathParameters,
  tLTenantUpdate: BodyType<TLTenantUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTenant>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateTenant>>,
          TError,
          Awaited<ReturnType<typeof updateTenant>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateTenant<
  TData = Awaited<ReturnType<typeof updateTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateTenantPathParameters,
  tLTenantUpdate: BodyType<TLTenantUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Tenant
 */

export function useUpdateTenant<
  TData = Awaited<ReturnType<typeof updateTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { tenantId }: UpdateTenantPathParameters,
  tLTenantUpdate: BodyType<TLTenantUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateTenantQueryOptions({ tenantId }, tLTenantUpdate, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Tenant
 */
export const prefetchUpdateTenantQuery = async <
  TData = Awaited<ReturnType<typeof updateTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { tenantId }: UpdateTenantPathParameters,
  tLTenantUpdate: BodyType<TLTenantUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateTenantQueryOptions({ tenantId }, tLTenantUpdate, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Tenant
 */
export const invalidateUpdateTenant = async (
  queryClient: QueryClient,
  { tenantId }: UpdateTenantPathParameters,
  tLTenantUpdate: BodyType<TLTenantUpdate>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getUpdateTenantQueryKey({ tenantId }, tLTenantUpdate) }, options);

  return queryClient;
};

/**
 * @summary Delete Tenant
 */
export const deleteTenant = (
  { tenantId }: DeleteTenantPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>({ url: `/tenants/${tenantId}`, method: 'DELETE', signal }, options);
};

export const getDeleteTenantQueryKey = ({ tenantId }: DeleteTenantPathParameters) => {
  return ['DELETE', 'tenants', tenantId] as const;
};

export const getDeleteTenantQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { tenantId }: DeleteTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteTenantQueryKey({ tenantId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteTenant>>> = ({ signal }) =>
    deleteTenant({ tenantId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: tenantId !== null && tenantId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteTenant>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteTenantQueryResult = NonNullable<Awaited<ReturnType<typeof deleteTenant>>>;
export type DeleteTenantQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteTenant<
  TData = Awaited<ReturnType<typeof deleteTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteTenantPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTenant>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteTenant>>,
          TError,
          Awaited<ReturnType<typeof deleteTenant>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteTenant<
  TData = Awaited<ReturnType<typeof deleteTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTenant>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteTenant>>,
          TError,
          Awaited<ReturnType<typeof deleteTenant>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteTenant<
  TData = Awaited<ReturnType<typeof deleteTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Tenant
 */

export function useDeleteTenant<
  TData = Awaited<ReturnType<typeof deleteTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { tenantId }: DeleteTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteTenantQueryOptions({ tenantId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Tenant
 */
export const prefetchDeleteTenantQuery = async <
  TData = Awaited<ReturnType<typeof deleteTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { tenantId }: DeleteTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteTenantQueryOptions({ tenantId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Tenant
 */
export const invalidateDeleteTenant = async (
  queryClient: QueryClient,
  { tenantId }: DeleteTenantPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteTenantQueryKey({ tenantId }) }, options);

  return queryClient;
};

/**
 * @summary Get Tenant
 */
export const getTenant = (
  { tenantId }: GetTenantPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLTenant>({ url: `/tenants/${tenantId}`, method: 'GET', signal }, options);
};

export const getGetTenantQueryKey = ({ tenantId }: GetTenantPathParameters) => {
  return ['tenants', tenantId] as const;
};

export const getGetTenantQueryOptions = <
  TData = Awaited<ReturnType<typeof getTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { tenantId }: GetTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTenantQueryKey({ tenantId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenant>>> = ({ signal }) =>
    getTenant({ tenantId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: tenantId !== null && tenantId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getTenant>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetTenantQueryResult = NonNullable<Awaited<ReturnType<typeof getTenant>>>;
export type GetTenantQueryError = ErrorType<TLHTTPValidationError>;

export function useGetTenant<TData = Awaited<ReturnType<typeof getTenant>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetTenantPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTenant>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof getTenant>>, TError, Awaited<ReturnType<typeof getTenant>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTenant<TData = Awaited<ReturnType<typeof getTenant>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTenant>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTenant>>,
          TError,
          Awaited<ReturnType<typeof getTenant>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTenant<TData = Awaited<ReturnType<typeof getTenant>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Tenant
 */

export function useGetTenant<TData = Awaited<ReturnType<typeof getTenant>>, TError = ErrorType<TLHTTPValidationError>>(
  { tenantId }: GetTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetTenantQueryOptions({ tenantId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Tenant
 */
export const prefetchGetTenantQuery = async <
  TData = Awaited<ReturnType<typeof getTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { tenantId }: GetTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetTenantQueryOptions({ tenantId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Tenant
 */
export const invalidateGetTenant = async (
  queryClient: QueryClient,
  { tenantId }: GetTenantPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetTenantQueryKey({ tenantId }) }, options);

  return queryClient;
};

/**
 * @summary Permanently Delete Tenant
 */
export const permanentlyDeleteTenant = (
  { tenantId }: PermanentlyDeleteTenantPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLTenantPurgeResponse>(
    { url: `/tenants/${tenantId}/permanent`, method: 'DELETE', signal },
    options,
  );
};

export const getPermanentlyDeleteTenantQueryKey = ({ tenantId }: PermanentlyDeleteTenantPathParameters) => {
  return ['DELETE', 'tenants', tenantId, 'permanent'] as const;
};

export const getPermanentlyDeleteTenantQueryOptions = <
  TData = Awaited<ReturnType<typeof permanentlyDeleteTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { tenantId }: PermanentlyDeleteTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof permanentlyDeleteTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getPermanentlyDeleteTenantQueryKey({ tenantId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof permanentlyDeleteTenant>>> = ({ signal }) =>
    permanentlyDeleteTenant({ tenantId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: tenantId !== null && tenantId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof permanentlyDeleteTenant>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type PermanentlyDeleteTenantQueryResult = NonNullable<Awaited<ReturnType<typeof permanentlyDeleteTenant>>>;
export type PermanentlyDeleteTenantQueryError = ErrorType<TLHTTPValidationError>;

export function usePermanentlyDeleteTenant<
  TData = Awaited<ReturnType<typeof permanentlyDeleteTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PermanentlyDeleteTenantPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof permanentlyDeleteTenant>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof permanentlyDeleteTenant>>,
          TError,
          Awaited<ReturnType<typeof permanentlyDeleteTenant>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePermanentlyDeleteTenant<
  TData = Awaited<ReturnType<typeof permanentlyDeleteTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PermanentlyDeleteTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof permanentlyDeleteTenant>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof permanentlyDeleteTenant>>,
          TError,
          Awaited<ReturnType<typeof permanentlyDeleteTenant>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePermanentlyDeleteTenant<
  TData = Awaited<ReturnType<typeof permanentlyDeleteTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PermanentlyDeleteTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof permanentlyDeleteTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Permanently Delete Tenant
 */

export function usePermanentlyDeleteTenant<
  TData = Awaited<ReturnType<typeof permanentlyDeleteTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { tenantId }: PermanentlyDeleteTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof permanentlyDeleteTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPermanentlyDeleteTenantQueryOptions({ tenantId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Permanently Delete Tenant
 */
export const prefetchPermanentlyDeleteTenantQuery = async <
  TData = Awaited<ReturnType<typeof permanentlyDeleteTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { tenantId }: PermanentlyDeleteTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof permanentlyDeleteTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPermanentlyDeleteTenantQueryOptions({ tenantId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Permanently Delete Tenant
 */
export const invalidatePermanentlyDeleteTenant = async (
  queryClient: QueryClient,
  { tenantId }: PermanentlyDeleteTenantPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getPermanentlyDeleteTenantQueryKey({ tenantId }) }, options);

  return queryClient;
};

/**
 * @summary Restore Tenant
 */
export const restoreTenant = (
  { tenantId }: RestoreTenantPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>({ url: `/tenants/${tenantId}/restore`, method: 'POST', signal }, options);
};

export const getRestoreTenantQueryKey = ({ tenantId }: RestoreTenantPathParameters) => {
  return ['POST', 'tenants', tenantId, 'restore'] as const;
};

export const getRestoreTenantQueryOptions = <
  TData = Awaited<ReturnType<typeof restoreTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { tenantId }: RestoreTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof restoreTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRestoreTenantQueryKey({ tenantId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof restoreTenant>>> = ({ signal }) =>
    restoreTenant({ tenantId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: tenantId !== null && tenantId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof restoreTenant>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RestoreTenantQueryResult = NonNullable<Awaited<ReturnType<typeof restoreTenant>>>;
export type RestoreTenantQueryError = ErrorType<TLHTTPValidationError>;

export function useRestoreTenant<
  TData = Awaited<ReturnType<typeof restoreTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RestoreTenantPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof restoreTenant>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof restoreTenant>>,
          TError,
          Awaited<ReturnType<typeof restoreTenant>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRestoreTenant<
  TData = Awaited<ReturnType<typeof restoreTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RestoreTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof restoreTenant>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof restoreTenant>>,
          TError,
          Awaited<ReturnType<typeof restoreTenant>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRestoreTenant<
  TData = Awaited<ReturnType<typeof restoreTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RestoreTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof restoreTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Restore Tenant
 */

export function useRestoreTenant<
  TData = Awaited<ReturnType<typeof restoreTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { tenantId }: RestoreTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof restoreTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRestoreTenantQueryOptions({ tenantId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Restore Tenant
 */
export const prefetchRestoreTenantQuery = async <
  TData = Awaited<ReturnType<typeof restoreTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { tenantId }: RestoreTenantPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof restoreTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRestoreTenantQueryOptions({ tenantId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Restore Tenant
 */
export const invalidateRestoreTenant = async (
  queryClient: QueryClient,
  { tenantId }: RestoreTenantPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getRestoreTenantQueryKey({ tenantId }) }, options);

  return queryClient;
};

/**
 * @summary Update Tenant Visibility
 */
export const updateTenantVisibility = (
  { tenantId }: UpdateTenantVisibilityPathParameters,
  tLTenantVisibilityUpdateRequest: BodyType<TLTenantVisibilityUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLTenant>(
    {
      url: `/tenants/${tenantId}/visibility`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLTenantVisibilityUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateTenantVisibilityQueryKey = (
  { tenantId }: UpdateTenantVisibilityPathParameters,
  tLTenantVisibilityUpdateRequest?: BodyType<TLTenantVisibilityUpdateRequest>,
) => {
  return ['PATCH', 'tenants', tenantId, 'visibility', tLTenantVisibilityUpdateRequest] as const;
};

export const getUpdateTenantVisibilityQueryOptions = <
  TData = Awaited<ReturnType<typeof updateTenantVisibility>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { tenantId }: UpdateTenantVisibilityPathParameters,
  tLTenantVisibilityUpdateRequest: BodyType<TLTenantVisibilityUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTenantVisibility>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getUpdateTenantVisibilityQueryKey({ tenantId }, tLTenantVisibilityUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateTenantVisibility>>> = ({ signal }) =>
    updateTenantVisibility({ tenantId }, tLTenantVisibilityUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: tenantId !== null && tenantId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateTenantVisibility>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateTenantVisibilityQueryResult = NonNullable<Awaited<ReturnType<typeof updateTenantVisibility>>>;
export type UpdateTenantVisibilityQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateTenantVisibility<
  TData = Awaited<ReturnType<typeof updateTenantVisibility>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateTenantVisibilityPathParameters,
  tLTenantVisibilityUpdateRequest: BodyType<TLTenantVisibilityUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTenantVisibility>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateTenantVisibility>>,
          TError,
          Awaited<ReturnType<typeof updateTenantVisibility>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateTenantVisibility<
  TData = Awaited<ReturnType<typeof updateTenantVisibility>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateTenantVisibilityPathParameters,
  tLTenantVisibilityUpdateRequest: BodyType<TLTenantVisibilityUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTenantVisibility>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateTenantVisibility>>,
          TError,
          Awaited<ReturnType<typeof updateTenantVisibility>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateTenantVisibility<
  TData = Awaited<ReturnType<typeof updateTenantVisibility>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateTenantVisibilityPathParameters,
  tLTenantVisibilityUpdateRequest: BodyType<TLTenantVisibilityUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTenantVisibility>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Tenant Visibility
 */

export function useUpdateTenantVisibility<
  TData = Awaited<ReturnType<typeof updateTenantVisibility>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { tenantId }: UpdateTenantVisibilityPathParameters,
  tLTenantVisibilityUpdateRequest: BodyType<TLTenantVisibilityUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTenantVisibility>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateTenantVisibilityQueryOptions({ tenantId }, tLTenantVisibilityUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Tenant Visibility
 */
export const prefetchUpdateTenantVisibilityQuery = async <
  TData = Awaited<ReturnType<typeof updateTenantVisibility>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { tenantId }: UpdateTenantVisibilityPathParameters,
  tLTenantVisibilityUpdateRequest: BodyType<TLTenantVisibilityUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTenantVisibility>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateTenantVisibilityQueryOptions({ tenantId }, tLTenantVisibilityUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Tenant Visibility
 */
export const invalidateUpdateTenantVisibility = async (
  queryClient: QueryClient,
  { tenantId }: UpdateTenantVisibilityPathParameters,
  tLTenantVisibilityUpdateRequest: BodyType<TLTenantVisibilityUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateTenantVisibilityQueryKey({ tenantId }, tLTenantVisibilityUpdateRequest) },
    options,
  );

  return queryClient;
};
